import { Route } from '@/app/routes/(app)/recommendation-management/main-page/$store-list/$host-list';
import { useCan } from '@/components/can/Can';
import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { useDialogFormStore } from '@/components/dialog/form.store';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { useDynamicLocale } from '@/hoslog-intl';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import { HostList } from '@/services/mainPage/storeList/hostList/hostList.types';
import React from 'react';
import { ManageMainPageHostForm } from '../forms/ManageMainPageHostForm';
import useDeleteMainPageHost from '../hooks/useDeleteMainPageHost';
import { getColumns } from './Column';

export function HostListTable({
  data,
  pagination,
  loading,
}: {
  data: HostList[];
  pagination: Pagination;
  loading: boolean;
}) {
  const { t } = useDynamicLocale();
  const { openConfirmation } = useConfirmationStore();
  const { onOpen } = useDialogFormStore();
  const { mutateAsync: deleteHost } = useDeleteMainPageHost();
  const { locationId, displayOrderId, recommendationId } = Route.useSearch();

  const columns = React.useMemo(() => getColumns(t, openConfirmation, deleteHost), [t, openConfirmation, deleteHost]);
  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );

  const params = Route.useParams();
  const groupId = params['store-list'];
  const clubId = params['host-list'];
  const addedHosts = data.map((host) => {
    return {
      hostId: host.hostId,
    };
  });

  const onAddNew = () => {
    onOpen({
      title: 'Manage Recommendation',
      okText: t('common.page.modal.submit'),
      size: 'lg',
      modalTitle: 'common.page.modal.manageRecommendation.title',
      component: (
        <ManageMainPageHostForm
          clubId={clubId}
          groupId={groupId}
          locationId={locationId}
          addedHosts={addedHosts}
          displayOrderId={displayOrderId}
          recommendationId={recommendationId}
        />
      ),
    });
  };

  const canUpdateHost = useCan({ allOf: [PERMS.recommendation.mainPage.host.update] });

  const { table } = useDataTable({
    data: data,
    columns,
    pageCount: pageCount,
    meta: {
      title: t('recommendationManagement.hostList.table.title'),
      totalPage: pagination.totalItems,
      loading: loading,
      actionKey: canUpdateHost ? 'onAddNew' : '',
      action: onAddNew,
    },
    getRowId: (originalRow) => String(originalRow.hostId),
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <>
      <DataTable table={table} className="font-medium">
        <DataTableToolbar />
      </DataTable>
    </>
  );
}
